// src/shared/auth/useRbac.ts
// ────────────────────────────────────────────────────────────────────────────
// Centralised RBAC hook — single source of truth for capability checks.
//
// The BFF digests raw roles/permissions into a flat boolean capabilities map.
// This hook reads that map and exposes convenience helpers.
//
// Usage:
//   const { can, canAny, canAll, isReadOnly, isSuperadmin } = useRbac();
//   if (can('canAdminTableOps')) { ... }
//   const disabled = isReadOnly('tableops');
// ────────────────────────────────────────────────────────────────────────────
import { useMemo } from 'react';
import { useAuth } from './session';

/**
 * Domain → read-only capability key produced by the BFF.
 * True = the user holds NO write perms for that domain.
 */
const DOMAIN_RO_KEY: Record<string, string> = {
  tableops: 'isTableOpsReadOnly',
  ttlock:   'isTtlockReadOnly',
  workflow: 'isWorkflowReadOnly',
  iam:      'isIamReadOnly',
  system:   'isSystemReadOnly',
};

export function useRbac() {
  const { capabilities } = useAuth();

  const isSuperadmin = capabilities.isSuperadmin === true;

  /** True if user holds a specific capability (or is superadmin). */
  const can = useMemo(
    () => (cap: string) => isSuperadmin || capabilities[cap] === true,
    [isSuperadmin, capabilities],
  );

  /** True if user holds ANY of the given capabilities. */
  const canAny = useMemo(
    () => (...needed: string[]) => isSuperadmin || needed.some(k => capabilities[k] === true),
    [isSuperadmin, capabilities],
  );

  /** True if user holds ALL of the given capabilities. */
  const canAll = useMemo(
    () => (...needed: string[]) => isSuperadmin || needed.every(k => capabilities[k] === true),
    [isSuperadmin, capabilities],
  );

  /**
   * True if the user is view-only for a domain.
   * They can see the domain pages but cannot perform any write actions.
   */
  const isReadOnly = useMemo(
    () => (domain: string) => {
      if (isSuperadmin) return false;
      const key = DOMAIN_RO_KEY[domain];
      if (!key) return true;            // unknown domain → treat as read-only
      return capabilities[key] === true; // BFF sets true when user lacks ALL write perms
    },
    [isSuperadmin, capabilities],
  );

  return { can, canAny, canAll, isReadOnly, isSuperadmin, capabilities };
}
